/**
 * ActivityFeedItem — single row in Dashboard Activity-Feed.
 * Sprint B PR-B3 DK-18.
 *
 * Icon + DE-label + relative-time + optional asset/collection-context.
 * Click-through wenn assetId ODER collectionId vorhanden (backend liefert die
 * Felder NUR wenn user-access verified, siehe DK-9 — Frontend vertraut Backend).
 *
 * Defensive bei unknown event-key: getEventLabel-fallback rendert event-string
 * als label + 'help'-icon. Bei unknown icon-key: I.help fallback.
 */
import { Link } from 'wouter';
import type { ActivityFeedItem as ActivityFeedItemType } from '../../shared/dashboard-types';
import { getEventLabel } from '../../shared/audit-event-labels';
import { formatRelative } from '../lib/format-relative';
import { I, type IconKey } from '../lib/icons';

function iconFor(key: string) {
  return (I as Record<string, typeof I.help>)[key] ?? I.help;
}

export function ActivityFeedItem({ item }: { item: ActivityFeedItemType }) {
  const label = getEventLabel(item.event);
  const Icon = iconFor(label.icon);

  // Click-through priority: asset > collection. Beide gleichzeitig nur bei
  // collection.asset_removed möglich — dann wins assetId (action subject).
  const href = item.assetId
    ? `/assets/${item.assetId}`
    : item.collectionId
      ? `/collections/${item.collectionId}`
      : null;

  const subLabel =
    item.assetDisplayFilename ?? item.collectionName ?? null;

  const body = (
    <>
      {item.assetThumbUrl ? (
        <img
          src={item.assetThumbUrl}
          alt=""
          loading="lazy"
          className="w-10 h-10 rounded object-cover flex-shrink-0 border border-ink-100"
        />
      ) : (
        <div className="w-10 h-10 rounded bg-ink-50 flex items-center justify-center flex-shrink-0 text-brand-green-600">
          <Icon size={16} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-brand-green-700 truncate">
          {label.label_de}
        </div>
        <div className="text-xs2 text-brand-green-600 truncate">
          {formatRelative(item.createdAt)}
          {subLabel ? <> · {subLabel}</> : null}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-ink-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
      >
        {body}
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2">
      {body}
    </div>
  );
}

// Re-export IconKey for callers (DashboardPage skeleton uses I directly).
export type { IconKey };
